import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { UserX, Download, Ticket, RefreshCw } from "lucide-react";

const eur = (v) => `€ ${Number(v || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const inputCls = "bg-zinc-950 border-zinc-800 font-mono";

export const ElasticInactive = () => {
  const [days, setDays] = useState(60);
  const [minReceipts, setMinReceipts] = useState(2);
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState({});
  const [coupons, setCoupons] = useState([]);
  const [couponOpen, setCouponOpen] = useState(false);
  const [couponId, setCouponId] = useState("");
  const [assigning, setAssigning] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/elastic/inactive", { params: { days, min_receipts: minReceipts, limit: 200 } });
      setData(data);
      const sel = {};
      data.results.forEach((r) => { if (r.vivi_customer_id) sel[r.card] = true; });
      setSelected(sel);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const openCoupon = async () => {
    try { const { data } = await api.get("/coupons"); setCoupons(data.filter((c) => c.active !== false)); setCouponId(data[0]?.id || ""); setCouponOpen(true); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const selectedIds = (data?.results || []).filter((r) => selected[r.card] && r.vivi_customer_id).map((r) => r.vivi_customer_id);
  const assign = async () => {
    if (!couponId || selectedIds.length === 0) return;
    setAssigning(true);
    try {
      const { data: res } = await api.post(`/coupons/${couponId}/assign`, { customer_ids: selectedIds });
      toast.success(`Coupon di ritorno assegnato a ${res.assigned ?? selectedIds.length} clienti`);
      setCouponOpen(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setAssigning(false); }
  };
  const exportCsv = () => {
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = data.results.map((r) => [r.card, r.name, r.cell, r.email, r.city, r.receipts, r.total_eur, r.last_visit, r.inactive_days, r.marketing_ok ? "sì" : "no", r.vivi_customer_id ? "sì" : "no"]);
    const csv = [["Carta", "Cliente", "Cell", "Email", "Città", "Scontrini", "Speso", "Ultima visita", "Giorni inattivo", "Consenso marketing", "In Vivi"], ...rows].map((r) => r.map(esc).join(";")).join("\n");
    const url = URL.createObjectURL(new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a"); a.href = url; a.download = `crm_inattivi_${days}gg.csv`; a.click(); URL.revokeObjectURL(url);
  };
  const toggleAll = (on) => { const s = {}; (data?.results || []).forEach((r) => { if (r.vivi_customer_id) s[r.card] = on; }); setSelected(s); };

  return (
    <section className="card-elev p-4 space-y-3" data-testid="crm-inactive">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1"><UserX className="h-3.5 w-3.5 text-red-400" /> Alert clienti inattivi</div>
          <p className="text-xs text-zinc-500 mt-1">Clienti fedeltà del CRM che non acquistano da oltre N giorni, ordinati per spesa storica: i migliori candidati per un coupon di ritorno.</p>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex flex-wrap items-end gap-2">
          <div><Label className="text-zinc-400 text-xs">Inattivi da (giorni)</Label><Input type="number" min={7} max={730} data-testid="crm-inactive-days" value={days} onChange={(e) => setDays(parseInt(e.target.value) || 60)} className={`${inputCls} w-28`} /></div>
          <div><Label className="text-zinc-400 text-xs">Min. scontrini</Label><Input type="number" min={1} data-testid="crm-inactive-min" value={minReceipts} onChange={(e) => setMinReceipts(parseInt(e.target.value) || 1)} className={`${inputCls} w-24`} /></div>
          <Button type="submit" disabled={busy} data-testid="crm-inactive-refresh" className="bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)]"><RefreshCw className={`h-4 w-4 mr-1 ${busy ? "animate-spin" : ""}`} /> Aggiorna</Button>
        </form>
      </div>

      {data && (
        <>
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <span className="pill bg-red-500/15 text-red-400" data-testid="crm-inactive-total">{Number(data.total_inactive).toLocaleString("it-IT")} clienti inattivi da oltre {data.days} gg</span>
            <span className="text-zinc-500 text-xs">mostrati i primi {data.results.length} per spesa · {data.in_vivi} hanno già la carta in Vivi</span>
            <div className="ml-auto flex gap-2">
              <Button size="sm" variant="outline" onClick={exportCsv} data-testid="crm-inactive-export" className="border-zinc-800"><Download className="h-3.5 w-3.5 mr-1" /> CSV</Button>
              <Button size="sm" onClick={openCoupon} disabled={selectedIds.length === 0} data-testid="crm-inactive-coupon" className="bg-amber-500 hover:bg-amber-400 text-zinc-950"><Ticket className="h-3.5 w-3.5 mr-1" /> Coupon di ritorno ({selectedIds.length})</Button>
            </div>
          </div>
          <div className="overflow-x-auto rounded-xl border border-zinc-800 max-h-[420px]">
            <table className="w-full text-xs" data-testid="crm-inactive-table">
              <thead className="bg-zinc-950 text-zinc-500 uppercase tracking-wider text-[10px] sticky top-0">
                <tr>
                  <th className="p-2"><input type="checkbox" data-testid="crm-inactive-select-all" checked={selectedIds.length > 0 && selectedIds.length === data.in_vivi} onChange={(e) => toggleAll(e.target.checked)} /></th>
                  <th className="text-left p-2">Cliente</th><th className="text-left p-2">Carta</th><th className="text-left p-2">Cell</th>
                  <th className="text-right p-2">Scontrini</th><th className="text-right p-2">Speso</th><th className="text-left p-2">Ultima visita</th>
                  <th className="text-right p-2">Inattivo</th><th className="text-left p-2">Mktg</th><th className="text-left p-2">Vivi</th>
                </tr>
              </thead>
              <tbody>
                {data.results.map((r) => (
                  <tr key={r.card} className="border-t border-zinc-800/60 hover:bg-zinc-900/40" data-testid={`crm-inactive-row-${r.card}`}>
                    <td className="p-2 text-center">{r.vivi_customer_id ? <input type="checkbox" checked={!!selected[r.card]} onChange={(e) => setSelected({ ...selected, [r.card]: e.target.checked })} /> : null}</td>
                    <td className="p-2 font-semibold">{r.name || "—"}</td><td className="p-2 font-mono">{r.card}</td><td className="p-2 font-mono">{r.cell || "—"}</td>
                    <td className="p-2 text-right font-mono">{r.receipts}</td><td className="p-2 text-right font-mono">{eur(r.total_eur)}</td><td className="p-2">{r.last_visit}</td>
                    <td className="p-2 text-right font-mono text-red-400">{r.inactive_days} gg</td>
                    <td className="p-2">{r.marketing_ok ? <span className="text-emerald-400">sì</span> : <span className="text-zinc-500">no</span>}</td>
                    <td className="p-2">{r.vivi_customer_id ? <span className="text-emerald-400">{r.vivi_points} pt</span> : <span className="text-zinc-500">non importato</span>}</td>
                  </tr>
                ))}
                {data.results.length === 0 && <tr><td colSpan={10} className="p-3 text-center text-zinc-500">Nessun cliente inattivo con questi filtri</td></tr>}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-zinc-500">Il coupon può essere assegnato solo ai clienti già presenti in Vivi (carta importata). Per gli altri usa l'import CRM oppure esporta il CSV.</p>
        </>
      )}

      <Dialog open={couponOpen} onOpenChange={setCouponOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800" data-testid="crm-inactive-coupon-dialog">
          <DialogHeader><DialogTitle>Coupon di ritorno per {selectedIds.length} clienti</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label className="text-zinc-400 text-xs">Scegli il coupon da assegnare</Label>
            <select data-testid="crm-inactive-coupon-select" value={couponId} onChange={(e) => setCouponId(e.target.value)} className="w-full h-10 rounded-md bg-zinc-950 border border-zinc-800 px-3 text-sm">
              {coupons.length === 0 && <option value="">Nessun coupon attivo: creane uno in Coupon</option>}
              {coupons.map((c) => <option key={c.id} value={c.id}>{c.title || c.name || c.code} {c.discount_value ? `· ${c.discount_type === "percent" ? `${c.discount_value}%` : `€ ${c.discount_value}`}` : ""}</option>)}
            </select>
            <p className="text-[11px] text-zinc-500">I clienti riceveranno la notifica coupon secondo le automazioni attive (push / WhatsApp).</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCouponOpen(false)} className="border-zinc-800">Annulla</Button>
            <Button onClick={assign} disabled={!couponId || assigning} data-testid="crm-inactive-coupon-confirm" className="bg-amber-500 hover:bg-amber-400 text-zinc-950">{assigning ? "Assegno…" : "Assegna coupon"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default ElasticInactive;
